import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Trash2, ChevronLeft, RefreshCw, AlertCircle, ExternalLink } from 'lucide-react';
import { format, parseISO } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface HistoryEntry {
  id: string;
  user_id: string;
  tool_id: string;
  tool_name: string;
  created_at: string; // ISO 8601
  input_summary: string;
  output_preview: string;
  full_output: string;
  snapshot?: string | null; // only present on single-entry fetch
}

// Map tool_id → the app's activeTool key
const TOOL_ID_TO_ROUTE: Record<string, string> = {
  'video-subtitles':      'video-subtitles',
  'ai-captions':          'ai-captions',
  'ai-blog':              'ai-blog',
  'ai-ideas':             'ai-ideas',
  'ai-product':           'ai-product',
  'ig-audit':             'ig-audit',
  'ig-compare':           'ig-compare',
  'ai-transcribe':        'ai-transcribe',
  'web-intel':            'web-intel',
  'website-report':       'website-report',
};

interface HistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTool: (toolId: string) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return format(parseISO(iso), 'dd MMM yyyy, HH:mm');
  } catch {
    return iso;
  }
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

const SkeletonRow: React.FC = () => (
  <div className="px-4 py-3 border-b border-white/[0.05] animate-pulse">
    <div className="h-3 rounded-full bg-white/10 w-1/3 mb-2" />
    <div className="h-2.5 rounded-full bg-white/[0.07] w-1/4 mb-2" />
    <div className="h-2.5 rounded-full bg-white/[0.07] w-3/4" />
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const HistoryPanel: React.FC<HistoryPanelProps> = ({ isOpen, onClose, onSelectTool }) => {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<HistoryEntry | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false); // true while fetching full entry
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const LIMIT = 20;

  // ── Fetch list ─────────────────────────────────────────────────────────────

  const fetchHistory = useCallback(async (pageNum: number, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }

    try {
      const res = await fetch(`/api/history?page=${pageNum}&limit=${LIMIT}`, {
        credentials: 'include',
      });

      if (!res.ok) throw new Error(`Failed to load history (${res.status})`);

      const json: { data: HistoryEntry[]; total: number; page: number; limit: number } =
        await res.json();

      setEntries((prev) => (append ? [...prev, ...json.data] : json.data));
      setTotal(json.total);
      setPage(json.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Fetch on open; reset state when closed
  useEffect(() => {
    if (isOpen) {
      setEntries([]);
      setPage(1);
      setTotal(0);
      setSelectedEntry(null);
      setDeleteConfirmId(null);
      fetchHistory(1, false);
    }
  }, [isOpen, fetchHistory]);

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      const res = await fetch(`/api/history/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      setEntries((prev) => prev.filter((e) => e.id !== id));
      setTotal((t) => t - 1);
      if (selectedEntry?.id === id) setSelectedEntry(null);
    } catch {
      // Entry stays in list on failure (per design)
    } finally {
      setDeleting(false);
      setDeleteConfirmId(null);
    }
  };

  // ── Select entry — fetch full entry (with snapshot) ────────────────────────

  const handleSelectEntry = async (entry: HistoryEntry) => {
    // Show list version immediately, then fetch full entry that includes snapshot
    setSelectedEntry(entry);
    setSnapshotLoading(true);
    try {
      const res = await fetch(`/api/history/${entry.id}`, { credentials: 'include' });
      if (res.ok) {
        const full: HistoryEntry = await res.json();
        setSelectedEntry(full);
      }
    } catch {
      // Keep showing the list version — snapshot just won't be available
    } finally {
      setSnapshotLoading(false);
    }
  };

  // ── Open in Tool ───────────────────────────────────────────────────────────

  const handleOpenInTool = async () => {
    if (!selectedEntry) return;
    const routeKey = TOOL_ID_TO_ROUTE[selectedEntry.tool_id];
    if (!routeKey) return;

    setRestoring(true);
    try {
      // Fetch full entry to get the snapshot (may already be loaded from handleSelectEntry)
      let entry = selectedEntry;
      if (!entry.snapshot) {
        const res = await fetch(`/api/history/${entry.id}`, { credentials: 'include' });
        if (res.ok) entry = await res.json();
      }

      const payload = entry.snapshot
        ? JSON.stringify({ toolId: entry.tool_id, snapshot: entry.snapshot })
        : null;

      if (payload) {
        // 1. sessionStorage — picked up by useEffect on fresh mount (navigating FROM another page)
        sessionStorage.setItem('pendingHistoryRestore', payload);

        // 2. window event — picked up by listener when tool is ALREADY mounted (already on that page)
        window.dispatchEvent(
          new CustomEvent('aadinHistoryRestore', {
            detail: { toolId: entry.tool_id, snapshot: entry.snapshot },
          }),
        );
      }

      // Navigate to the tool (closes panel via MainLayout's onSelectTool wrapper)
      onSelectTool(routeKey);
      onClose();
    } catch (err) {
      console.warn('[HistoryPanel] Open in Tool failed:', err);
    } finally {
      setRestoring(false);
    }
  };

  // ── Load more ──────────────────────────────────────────────────────────────

  const handleLoadMore = () => {
    const nextPage = page + 1;
    fetchHistory(nextPage, true);
  };

  const hasMore = entries.length < total;

  // ── Render ─────────────────────────────────────────────────────────────────

  // "Open in Tool" only works when we have a snapshot — without it the tool
  // would open blank which confuses users. Entries created after the latest
  // server restart will have snapshots; older entries won't.
  const hasRoute = selectedEntry && !!TOOL_ID_TO_ROUTE[selectedEntry.tool_id];
  const hasSnapshot = !!selectedEntry?.snapshot;
  const canOpenInTool = hasRoute && hasSnapshot;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="history-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Slide-over panel */}
          <motion.div
            key="history-panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="fixed inset-y-0 right-0 z-50 w-full max-w-md flex flex-col"
            style={{
              background: 'rgba(13,18,28,0.97)',
              backdropFilter: 'blur(32px) saturate(180%)',
              WebkitBackdropFilter: 'blur(32px) saturate(180%)',
              borderLeft: '1px solid rgba(255,255,255,0.07)',
              boxShadow: '-8px 0 40px rgba(0,0,0,0.5)',
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 py-4 shrink-0"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
            >
              {selectedEntry ? (
                <button
                  onClick={() => setSelectedEntry(null)}
                  className="flex items-center gap-1.5 text-white/50 hover:text-white/80 transition-colors text-sm"
                >
                  <ChevronLeft size={16} />
                  Back
                </button>
              ) : (
                <h2 className="text-sm font-semibold text-white/80 tracking-wide">History</h2>
              )}
              <button
                onClick={onClose}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors text-white/40 hover:text-white/70"
                aria-label="Close history panel"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {/* ── Detail view ── */}
              {selectedEntry ? (
                <div className="px-5 py-4 space-y-4">
                  <div>
                    <p className="text-[10px] text-white/30 uppercase tracking-widest mb-1">Tool</p>
                    <p className="text-sm font-medium text-white/85">{selectedEntry.tool_name}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/30 uppercase tracking-widest mb-1">Date</p>
                    <p className="text-sm text-white/60">{formatDate(selectedEntry.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/30 uppercase tracking-widest mb-1">Input</p>
                    <p className="text-sm text-white/60 leading-relaxed whitespace-pre-wrap break-words">
                      {selectedEntry.input_summary}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-white/30 uppercase tracking-widest mb-1">Preview</p>
                    <div
                      className="rounded-xl p-3 text-sm text-white/70 leading-relaxed whitespace-pre-wrap break-words"
                      style={{
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.07)',
                      }}
                    >
                      {selectedEntry.output_preview || selectedEntry.full_output}
                    </div>
                  </div>

                  {/* Open in Tool / restore status */}
                  {hasRoute && (
                    snapshotLoading ? (
                      // Still fetching the full entry
                      <div className="flex items-center justify-center gap-2 py-2 text-xs text-white/35">
                        <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Checking for saved data…
                      </div>
                    ) : canOpenInTool ? (
                      // Has snapshot — full restore available
                      <button
                        onClick={handleOpenInTool}
                        disabled={restoring}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all disabled:opacity-60"
                        style={{
                          background: 'linear-gradient(135deg, #54c4b6 0%, #216974 100%)',
                          color: '#fff',
                          boxShadow: '0 4px 16px rgba(84,196,182,0.3)',
                        }}
                      >
                        <ExternalLink size={15} />
                        {restoring ? 'Opening…' : 'Open in Tool'}
                      </button>
                    ) : (
                      // No snapshot — entry is too old (created before this feature)
                      <div
                        className="rounded-xl px-4 py-3 text-xs text-white/40 text-center leading-relaxed"
                        style={{
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.07)',
                        }}
                      >
                        This entry was saved before full restore was supported.
                        Transcribe the video again to enable "Open in Tool".
                      </div>
                    )
                  )}
                </div>
              ) : (
                <>
                  {/* ── Loading skeleton ── */}
                  {loading && (
                    <>
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                    </>
                  )}

                  {/* ── Error state ── */}
                  {!loading && error && (
                    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
                      <AlertCircle size={28} className="text-red-400/70" />
                      <p className="text-sm text-white/50">{error}</p>
                      <button
                        onClick={() => fetchHistory(1, false)}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium transition-all"
                        style={{
                          background: 'rgba(255,255,255,0.07)',
                          color: 'rgba(255,255,255,0.7)',
                          border: '1px solid rgba(255,255,255,0.1)',
                        }}
                      >
                        <RefreshCw size={13} />
                        Retry
                      </button>
                    </div>
                  )}

                  {/* ── Empty state ── */}
                  {!loading && !error && entries.length === 0 && (
                    <div className="flex items-center justify-center px-6 py-16 text-center">
                      <p className="text-sm text-white/35 leading-relaxed">
                        No history yet. Start using tools to see your activity here.
                      </p>
                    </div>
                  )}

                  {/* ── Entry list ── */}
                  {!loading && !error && entries.length > 0 && (
                    <ul>
                      {entries.map((entry) => (
                        <li
                          key={entry.id}
                          style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                        >
                          {/* Delete confirmation overlay */}
                          {deleteConfirmId === entry.id ? (
                            <div className="px-4 py-3 flex items-center justify-between gap-3">
                              <p className="text-xs text-white/60">Delete this entry?</p>
                              <div className="flex items-center gap-2 shrink-0">
                                <button
                                  onClick={() => setDeleteConfirmId(null)}
                                  disabled={deleting}
                                  className="px-3 py-1.5 rounded-lg text-xs text-white/50 hover:text-white/80 transition-colors"
                                  style={{ background: 'rgba(255,255,255,0.06)' }}
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleDelete(entry.id)}
                                  disabled={deleting}
                                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                                  style={{
                                    background: 'rgba(239,68,68,0.2)',
                                    color: '#f87171',
                                    border: '1px solid rgba(239,68,68,0.25)',
                                  }}
                                >
                                  {deleting ? 'Deleting…' : 'Delete'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="group flex items-start gap-2 px-4 py-3 hover:bg-white/[0.03] transition-colors cursor-pointer">
                              {/* Entry content */}
                              <button
                                className="flex-1 text-left min-w-0"
                                onClick={() => handleSelectEntry(entry)}
                              >
                                <p className="text-[13px] font-medium text-white/80 truncate">
                                  {entry.tool_name}
                                </p>
                                <p className="text-[11px] text-white/35 mt-0.5">
                                  {formatDate(entry.created_at)}
                                </p>
                                {entry.input_summary && (
                                  <p className="text-[11px] text-white/40 mt-0.5 truncate">
                                    {entry.input_summary}
                                  </p>
                                )}
                                {entry.output_preview && (
                                  <p className="text-[12px] text-white/45 mt-1.5 line-clamp-2 leading-relaxed">
                                    {entry.output_preview.substring(0, 120)}
                                  </p>
                                )}
                              </button>

                              {/* Delete icon */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirmId(entry.id);
                                }}
                                className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-red-500/15 text-white/30 hover:text-red-400"
                                aria-label="Delete entry"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* ── Load more ── */}
                  {!loading && !error && hasMore && (
                    <div className="px-4 py-4 flex justify-center">
                      <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="px-5 py-2 rounded-xl text-xs font-medium transition-all disabled:opacity-50"
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          color: 'rgba(255,255,255,0.6)',
                          border: '1px solid rgba(255,255,255,0.09)',
                        }}
                      >
                        {loadingMore ? 'Loading…' : 'Load more'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default HistoryPanel;
